/* global G */
import { curry, isObj, PlatformEvent } from 'lib/util'

/**
 * TranslationOptions
 *
 * @property ns           namespace
 * @property key          identifier
 * @property defaultValue fallback value if no translation is found
 */
type TranslationOptions = {
  ns: string,
  key: string,
  defaultValue: string,
  [key: string]: any
}

/**
 * TranslationMDOptions
 *
 * @extends TranslationOptions
 * @property [md]           whether to parse Markdown syntax
 */
type TranslationMDOptions = TranslationOptions & {
  md?: boolean,
}

/**
 * External dialog options configuration
 * to be managed by this handler.
 *
 * @property title    translatable Markdown text
 * @property text     translatable Markdown text
 * @property [ok]     either translatable text to use as label or a boolean toggling its display
 * @property [cancel] either translatable text to use as label or a boolean toggling its display
 */
export type DialogOptions = {
  title: TranslationMDOptions,
  text: TranslationMDOptions,
  ok?: TranslationOptions | boolean,
  cancel?: TranslationOptions | boolean,
}

/**
 * Internal dialog options configuration to be
 * passed to and managed by the `Confirm` element.
 */
type DialogConfig = {
  title: string,
  text: string,
  children: {
    ok?: {
      key: string,
      value: string,
    },
    cancel?: {
      key: string,
      value: string,
    },
  }
  okHandler?: () => void,
  cancelHandler?: () => void,
}

/**
 * Default dialog options
 *
 * Will be used if the provided configuration doesn't have `title` and `text` set.
 */
const _defaultDialogConfig = (component: Gaia.Component.Spec | null): DialogOptions => ({
  title: {
    ns: 'common',
    key: 'dialog.error.generic.title',
    defaultValue: component?.[G.PROPS]?.label?.dialog?.title
        || 'Error',
  },
  text: {
    ns: 'common',
    key: 'dialog.error.generic.text',
    defaultValue: component?.[G.PROPS]?.label?.dialog?.title
        || 'An error has occurred, please try again later.',
  },
  ok: true,
  cancel: false,
})

/**
 * Default configuration for the dialog.
 *
 * @param module    app module
 * @param component action component
 * @param options   configuration used
 * @private
 */
const _dialogConfig = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec | null,
  options: DialogOptions,
): Promise<DialogConfig> => {
  const { title, text, ok = true, cancel = true } = options

  const okObj = isObj(ok) ? ok : {} as any
  const cancelObj = isObj(cancel) ? cancel : {} as any

  return {
    title: await module[G.ADAPTER][G.INTL].markdown(
      title.key,
      {
        ...title,
        ns: title.ns,
        _key: title.key,
        defaultValue: title.defaultValue,
        md: title.md,
      },
    ),
    text: await module[G.ADAPTER][G.INTL].markdown(
      text?.key,
      {
        ...text,
        ns: text.ns,
        _key: text.key,
        defaultValue: text.defaultValue,
        md: text.md,
      },
    ),
    children: {
      ...ok && {
        ok: {
          key: okObj.key || 'ok',
          value: await module[G.ADAPTER][G.INTL]._t(
            okObj.key || 'button.ok',
            {
              ...okObj,
              ns: okObj.ns || 'common',
              _key: okObj.key || 'button.ok',
              defaultValue: okObj.defaultValue || 'Ok',
            },
          ),
        },
      },
      ...cancel && {
        cancel: {
          key: cancelObj.key || 'cancel',
          value: await module[G.ADAPTER][G.INTL]._t(
            cancelObj.key || 'button.cancel',
            {
              ...cancelObj,
              ns: cancelObj.ns || 'common',
              _key: cancelObj.key || 'button.cancel',
              defaultValue: cancelObj.defaultValue || 'Cancel',
            },
          ),
        },
      },
    },
  }
}

/**
 * Returns a function that should be used to trigger the event showing the dialog. Will also check
 * if at least one option ('Ok or 'Cancel') is being present. If not, it will throw an {@link Error}
 * since this would mean the dialog is not dismissible.
 *
 * @param module    app module
 * @param component action component
 * @param options   configuration for the dialog
 * @private
 */
const _createDialog = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec | null,
  options: DialogOptions,
) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]
  const { ok = true, cancel = true } = options

  if (!ok && !cancel) throw new Error('The dialog needs at least one option')

  const dialogConfig = await _dialogConfig(module, component, options)

  return (actions: object) => {
    eventBus.dispatch(eventBus.type(G.DATA, G.UNDO), {
      ...dialogConfig,
      ...actions,
    })
  }
}

/**
 * Non-Blocking Dialog Event Handler
 *
 * Will display a non-blocking dialog (meaning code execution will continue). It will display an
 * 'Ok' option only since having a choice doesn't make sense for a non-blocking dialog. It should
 * purely be used to inform the user about something. It can be configured through `event`
 * like this:
 *
 * @example
 * {
 *     title: {
 *         ns: 'ticket' (will default to common)
 *         key: 'path.to.translation' (will default to dialog.generic.error.title)
 *         defaultValue: '...', (optional)
 *     }
 *     text: {
 *         ns: 'ticket' (will default to common)
 *         key: 'path.to.translation' (will default to dialog.generic.error.title)
 *         defaultValue: '...', (optional)
 *     }
 * }
 *
 * @param module          app module
 * @param component       action component
 * @param event           event or configuration
 * @param [event.detail]  specific configuration for the dialog
 */
const showDialog = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: PlatformEvent<DialogOptions>,
) => {
  const config = event?.detail || event || {}
  const options = (config?.text && config?.title && config)
      || _defaultDialogConfig(component)

  try {
    (await _createDialog(module, component, { ...options, cancel: false }))({ cancel: false })
  } catch (e) {
    console.error(e)
  }
}

/**
 * Blocking Dialog Event Handler
 *
 * Will display a blocking dialog (meaning code execution is halted until the user chooses an
 * option). By default, it will display a `Ok` and `Cancel` option, returning `true` and `false`
 * respectively. It can be configured through `event` like this:
 *
 * @example
 * {
 *     ok: true|false,
 *     cancel: true|false,
 *     title: {
 *         ns: 'ticket' (will default to common)
 *         key: 'path.to.translation' (will default to dialog.generic.error.title)
 *         defaultValue: '...', (optional)
 *     }
 *     text: {
 *         ns: 'ticket' (will default to common)
 *         key: 'path.to.translation' (will default to dialog.generic.error.title)
 *         defaultValue: '...', (optional)
 *     }
 * }
 *
 * @param module          app module
 * @param component       action component
 * @param event           event or configuration
 * @param [event.detail]  specific configuration for the dialog
 */
export const showBlockingDialog = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec | null,
  event: PlatformEvent<DialogOptions> | DialogOptions,
): Promise<boolean|undefined> => {
  const config = 'detail' in event ? event.detail || {}: event || {}
  const options = (config?.text && config?.title && config)
      || _defaultDialogConfig(component)

  const { ok = true, cancel = true } = options

  try {
    const dialogEvent = await _createDialog(module, component, options)

    return await new Promise((resolve) => {
      dialogEvent({
        /**
           * We could allow for configurable custom event handlers, like
           * @example
           * {
           *     events: {
           *         onDialogConfirm: '...',
           *         onDialogCancel: '...',
           *     }
           * }
           *
           * But I don't think it's really necessary, because we can always check the return value
           * of this handler and then execute some logic accordingly.
           */
        ...ok && { okHandler: () => { resolve(true) } },
        ...cancel && { cancelHandler: () => { resolve(false) } },
      })
    })
  } catch (e) {
    console.error(e)
  }

  return undefined
}

export default curry(showDialog)
